import { createSlice } from "@reduxjs/toolkit";
import {
  getDoctorNotificationsThunk,
  markDoctorNotificationReadThunk,
  markAllDoctorNotificationsReadThunk,
  deleteDoctorNotificationThunk,
  deleteAllDoctorNotificationsThunk,
} from "../thunk";
import { logout } from "../slices";

/**
 * The CuoreMD notification inbox (the 21 Aug 2026 spec's D-series rows).
 *
 * TWO SOURCES, ONE LIST. Rows arrive either from GET /api/web/notifications (the durable
 * truth, fetched on mount) or from the notification:new socket event (live, for a console that
 * happens to be open). The socket is deliberately NOT trusted as the source of truth — a doctor
 * with the tab closed receives no events at all — so `receiveLive` only ever PREPENDS, and a
 * refetch always wins. That is also why it de-duplicates by _id: a row can legitimately arrive
 * both ways if an event lands mid-fetch.
 */
const initialState = {
  items: [],
  unreadCount: 0,
  total: 0,
  page: 1,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    /** A notification:new socket event. Prepends unless we already hold that row. */
    receiveLive: (state, action) => {
      const note = action.payload;
      if (!note || !note._id) return;
      if (state.items.some((n) => String(n._id) === String(note._id))) return;
      state.items.unshift(note);
      state.total += 1;
      if (!note.read) state.unreadCount += 1;
    },
    clearNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDoctorNotificationsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDoctorNotificationsThunk.fulfilled, (state, action) => {
        state.loading = false;
        const { notifications, unreadCount, total, page } = action.payload || {};
        state.items = Array.isArray(notifications) ? notifications : [];
        state.unreadCount = Number(unreadCount) || 0;
        state.total = Number(total) || 0;
        state.page = Number(page) || 1;
      })
      .addCase(getDoctorNotificationsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Could not load notifications";
      })

      // Read state is applied optimistically on the server response rather than on dispatch,
      // so a failed request never leaves the badge showing a count the server disagrees with.
      .addCase(markDoctorNotificationReadThunk.fulfilled, (state, action) => {
        const id = String(action.payload?.id || "");
        const row = state.items.find((n) => String(n._id) === id);
        if (row && !row.read) {
          row.read = true;
          row.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllDoctorNotificationsReadThunk.fulfilled, (state) => {
        state.items.forEach((n) => {
          n.read = true;
        });
        state.unreadCount = 0;
      })
      .addCase(deleteDoctorNotificationThunk.fulfilled, (state, action) => {
        const { id, wasUnread } = action.payload || {};
        state.items = state.items.filter((n) => String(n._id) !== String(id));
        state.total = Math.max(0, state.total - 1);
        // The server reports wasUnread precisely because the client cannot infer it once the
        // row is gone from the list.
        if (wasUnread) state.unreadCount = Math.max(0, state.unreadCount - 1);
      })
      /* "Delete all" empties the inbox outright, so the counters go to ZERO rather than being
         decremented: the server deleted every row this doctor has, including ones not on the
         page in memory, and subtracting page-by-page would leave a non-zero total above an
         empty list. */
      .addCase(deleteAllDoctorNotificationsThunk.fulfilled, (state) => {
        state.items = [];
        state.total = 0;
        state.unreadCount = 0;
      })

      // A logged-out console must not keep another doctor's rows in memory.
      .addCase(logout, () => initialState);
  },
});

export const { receiveLive, clearNotifications } = notificationsSlice.actions;
export default notificationsSlice.reducer;
